import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Async thunks
export const runAssignment = createAsyncThunk(
  "assignment/runAssignment",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await axios.post("/assignment/run", requestData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to run assignment"
      );
    }
  }
);

export const createAssignmentJob = createAsyncThunk(
  "assignment/createJob",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await axios.post("/assignment/job", requestData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to create assignment job"
      );
    }
  }
);

export const runAssignmentJob = createAsyncThunk(
  "assignment/runJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/assignment/job/${jobId}/run`);
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to run assignment job"
      );
    }
  }
);

export const getAssignmentJobStatus = createAsyncThunk(
  "assignment/getJobStatus",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/assignment/job/${jobId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to get job status"
      );
    }
  }
);

export const getAllAssignmentJobs = createAsyncThunk(
  "assignment/getAllJobs",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/assignment/jobs");
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to get all jobs");
    }
  }
);

export const deleteAssignmentJob = createAsyncThunk(
  "assignment/deleteJob",
  async (jobId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`/assignment/job/${jobId}`);
      return { jobId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete job");
    }
  }
);

export const runDemoAssignment = createAsyncThunk(
  "assignment/runDemo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post("/assignment/demo");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to run demo assignment"
      );
    }
  }
);

const initialState = {
  currentAssignment: null,
  jobs: [],
  demoResult: null,
  loading: false,
  error: null,
  pollingJobs: [],
};

const assignmentSlice = createSlice({
  name: "assignment",
  initialState,
  reducers: {
    clearAssignmentData: (state) => {
      state.currentAssignment = null;
      state.demoResult = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    addPollingJob: (state, action) => {
      if (!state.pollingJobs.includes(action.payload)) {
        state.pollingJobs.push(action.payload);
      }
    },
    removePollingJob: (state, action) => {
      state.pollingJobs = state.pollingJobs.filter(
        (id) => id !== action.payload
      );
    },
    updateJobStatus: (state, action) => {
      const { jobId, status, result, error } = action.payload;
      const jobIndex = state.jobs.findIndex((job) => job.job_id === jobId);
      if (jobIndex !== -1) {
        state.jobs[jobIndex].status = status;
        if (result) state.jobs[jobIndex].result = result;
        if (error) state.jobs[jobIndex].error = error;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Run assignment
      .addCase(runAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(runAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAssignment = action.payload;
      })
      .addCase(runAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create assignment job
      .addCase(createAssignmentJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignmentJob.fulfilled, (state, action) => {
        state.loading = false;
        // Job will be added when we get its status
      })
      .addCase(createAssignmentJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Run assignment job
      .addCase(runAssignmentJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(runAssignmentJob.fulfilled, (state, action) => {
        state.loading = false;
        state.addPollingJob(action.payload.job_id);
      })
      .addCase(runAssignmentJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get assignment job status
      .addCase(getAssignmentJobStatus.fulfilled, (state, action) => {
        const jobIndex = state.jobs.findIndex(
          (job) => job.job_id === action.payload.job_id
        );
        if (jobIndex !== -1) {
          state.jobs[jobIndex] = action.payload;
        } else {
          state.jobs.push(action.payload);
        }

        // Remove from polling if completed or failed
        if (
          action.payload.status === "completed" ||
          action.payload.status === "failed"
        ) {
          state.pollingJobs = state.pollingJobs.filter(
            (id) => id !== action.payload.job_id
          );
        }
      })
      .addCase(getAssignmentJobStatus.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Get all assignment jobs
      .addCase(getAllAssignmentJobs.fulfilled, (state, action) => {
        state.jobs = action.payload.jobs || [];
      })
      .addCase(getAllAssignmentJobs.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete assignment job
      .addCase(deleteAssignmentJob.fulfilled, (state, action) => {
        state.jobs = state.jobs.filter(
          (job) => job.job_id !== action.payload.job_id
        );
        state.pollingJobs = state.pollingJobs.filter(
          (id) => id !== action.payload.job_id
        );
      })
      .addCase(deleteAssignmentJob.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Run demo assignment
      .addCase(runDemoAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(runDemoAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.demoResult = action.payload;
      })
      .addCase(runDemoAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearAssignmentData,
  clearError,
  addPollingJob,
  removePollingJob,
  updateJobStatus,
} = assignmentSlice.actions;

export default assignmentSlice.reducer;
